import React, { useEffect, useState } from "react";
import { Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import Highcharts from "highcharts";
import Chart from "highcharts-react-official";
import api from "./api";

const formatDate = (value: string | null | undefined) => value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";

const AccountCancellations = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [period, setPeriod] = useState("last6Months");
  const [statistics, setStatistics] = useState<any>({ monthly: [], byReason: [] });

  useEffect(() => {
    api.load(period).then((data) => {
      setRows([...data].sort((a, b) => new Date(b.cancellationDate).getTime() - new Date(a.cancellationDate).getTime()));
    });
  }, [period]);

  useEffect(() => {
    api.statistics(period).then(setStatistics);
  }, [period]);

  const monthlyCategories = statistics.monthly.map((item: any) => moment(`${item.month}-01`).format("MMM YYYY"));
  const monthlyValues = statistics.monthly.map((item: any) => item.count);
  const reasonData = statistics.byReason.map((item: any) => ({ name: item.reason, y: item.count }));
  const totalCancellations = monthlyValues.reduce((total: number, value: number) => total + value, 0);

  const handleExport = () => {
    const headers = ["Fecha de cancelación", "Nombre", "Correo", "Teléfono", "Stripe Customer ID", "Motivo", "Detalles", "Opinión adicional", "Órdenes", "Vencimiento membresía"];
    const values = rows.map((row) => [
      formatDate(row.cancellationDate), row.accountFullname, row.accountEmail, row.accountPhone,
      row.stripeCustomerId, row.cancellationReasonTitle, row.details, row.additionalOpinion,
      row.accountOrdersCount, formatDate(row.membershipEndDate),
    ]);
    const csv = [headers, ...values]
      .map((line) => line.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cancelaciones-${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid item><Typography sx={{ fontSize: "2rem", fontWeight: "bold" }}>Cancelaciones</Typography></Grid>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Periodo</InputLabel>
            <Select value={period} label="Periodo" onChange={(event) => setPeriod(event.target.value)}>
              <MenuItem value="thisMonth">Este mes</MenuItem>
              <MenuItem value="last3Months">Últimos 3 meses</MenuItem>
              <MenuItem value="last6Months">Últimos 6 meses</MenuItem>
              <MenuItem value="lastYear">Último año</MenuItem>
              <MenuItem value="all">Todo el histórico</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Card sx={{ width: "100%", maxWidth: 420, mb: 2, borderRadius: 2, color: "white", background: "linear-gradient(135deg, #8B2B96 0%, #B65AC0 100%)", boxShadow: "0 5px 16px rgba(139, 43, 150, .22)" }}>
        <CardContent sx={{ py: "18px !important" }}>
          <Typography sx={{ fontSize: "0.9rem", opacity: 0.9 }}>Total de cancelaciones</Typography>
          <Typography sx={{ fontSize: "2.25rem", lineHeight: 1.15, fontWeight: 800 }}>{totalCancellations}</Typography>
          <Typography sx={{ fontSize: "0.78rem", opacity: 0.85, mt: 0.5 }}>
            Suma de las cancelaciones mostradas en las gráficas y la lista
          </Typography>
        </CardContent>
      </Card>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Chart highcharts={Highcharts} options={{
              chart: { type: "column", height: 300 },
              title: { text: "Cancelaciones por mes" },
              xAxis: { categories: monthlyCategories },
              yAxis: { title: { text: "Cancelaciones" }, allowDecimals: false },
              legend: { enabled: false },
              series: [{ name: "Cancelaciones", data: monthlyValues, color: "#8B2B96" }],
              credits: { enabled: false },
            }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Chart highcharts={Highcharts} options={{
              chart: { type: "pie", height: 300 },
              title: { text: "Cancelaciones por motivo" },
              series: [{ name: "Cancelaciones", data: reasonData, showInLegend: true, dataLabels: { enabled: true, format: "{point.name}: {point.y}" } }],
              credits: { enabled: false },
            }} />
          </Paper>
        </Grid>
      </Grid>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Grid item><Typography sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>Historial de cancelaciones</Typography></Grid>
        <Grid item><Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!rows.length}>Exportar</Button></Grid>
      </Grid>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Fecha de cancelación</TableCell>
            <TableCell>Nombre</TableCell>
            <TableCell>Correo</TableCell>
            <TableCell>Teléfono</TableCell>
            <TableCell>Perfil de Stripe</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Detalles</TableCell>
            <TableCell>Opinión adicional</TableCell>
            <TableCell>Órdenes</TableCell>
            <TableCell>Vencimiento membresía</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow hover key={row.id}>
              <TableCell>{formatDate(row.cancellationDate)}</TableCell>
              <TableCell>{row.accountFullname || "-"}</TableCell>
              <TableCell>{row.accountEmail || "-"}</TableCell>
              <TableCell>{row.accountPhone || "-"}</TableCell>
              <TableCell>
                {row.stripeCustomerId ? (
                  <a
                    href={`https://dashboard.stripe.com/customers/${row.stripeCustomerId}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#635BFF", textDecoration: "none", fontWeight: 600 }}
                  >
                    Ver perfil
                  </a>
                ) : "-"}
              </TableCell>
              <TableCell>{row.cancellationReasonTitle || "-"}</TableCell>
              <TableCell sx={{ minWidth: 220, whiteSpace: "pre-line" }}>{row.details || "-"}</TableCell>
              <TableCell sx={{ minWidth: 220, whiteSpace: "pre-line" }}>{row.additionalOpinion || "-"}</TableCell>
              <TableCell>{row.accountOrdersCount ?? 0}</TableCell>
              <TableCell>{formatDate(row.membershipEndDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default AccountCancellations;
